// src/components/coach/charts/power-analysis-graph/PowerAnalysisControls.tsx
import React, { useRef, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Info } from 'lucide-react';
import { LeverMode } from '../../../../config/experiments';
import { setLeverMode } from '../../../../config/experiments';
import PowerAnalysisCalibrationButtons from './PowerAnalysisCalibrationButtons';
import { uiBtn } from '../../../ui/buttonClasses';

interface PowerAnalysisControlsProps {
  sessionId: string;
  hideDataSourceToggle: boolean;
  calibrationReady: boolean;
  hasCalibration: boolean;
  alpha: number;
  method: string | null;
  leverMode: LeverMode;
  showAdvanced: boolean;
  leverSuggestion: {
    suggestion: LeverMode | null;
    median: number | null;
    samples: number;
  } | null;
  onLeverModeToggle: () => void;
  onShowAdvancedToggle: () => void;
  onCalibrationComplete: () => void | Promise<void>;
  onCalibrationRemoved: () => void | Promise<void>;
  onCalibratingChange?: (isCalibrating: boolean) => void;
  containerWidth?: number; // Container width from parent chart component
  hideCalibrationControls?: boolean; // Hide calibration controls for rowers
}

const PowerAnalysisControls: React.FC<PowerAnalysisControlsProps> = ({
  sessionId,
  calibrationReady,
  hasCalibration,
  alpha,
  method,
  leverMode,
  showAdvanced,
  leverSuggestion,
  onLeverModeToggle,
  onShowAdvancedToggle,
  onCalibrationComplete,
  onCalibrationRemoved,
  onCalibratingChange,
  containerWidth = 800, // Use container width from parent, default to 800
  hideCalibrationControls = false, // Default to showing controls (coach view)
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const infoPopoverRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null
  );
  const [showInfo, setShowInfo] = useState(false);

  // Calculate responsive font sizes based on container width
  // Match legend logic: larger container = larger buttons, smaller container = smaller buttons
  const responsiveStyles = useMemo(() => {
    if (containerWidth < 400) {
      return {
        fontSize: '0.625rem', // 10px
        padding: { x: '0.375rem', y: '0.25rem' }, // px-1.5 py-1
        iconSize: '0.75rem', // 12px
        gap: 'gap-1',
      };
    }
    if (containerWidth < 600) {
      return {
        fontSize: '0.75rem', // 12px
        padding: { x: '0.5rem', y: '0.375rem' }, // px-2 py-1.5
        iconSize: '0.875rem', // 14px
        gap: 'gap-1.5',
      };
    }
    if (containerWidth < 800) {
      return {
        fontSize: '0.8125rem', // 13px
        padding: { x: '0.625rem', y: '0.4375rem' }, // px-2.5 py-1.75
        iconSize: '1rem', // 16px
        gap: 'gap-2',
      };
    }
    // Large screens
    return {
      fontSize: '0.875rem', // 14px (text-sm)
      padding: { x: '0.75rem', y: '0.5rem' }, // px-3 py-2
      iconSize: '1rem', // 16px
      gap: 'gap-2',
    };
  }, [containerWidth]);

  // Calculate dropdown position based on button position with boundary detection
  useEffect(() => {
    if (showAdvanced && buttonRef.current) {
      const updatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const dropdownWidth = 320; // max-w-[320px]
          const viewportWidth = window.innerWidth;
          const scrollX = window.scrollX;
          const scrollY = window.scrollY;

          // Calculate initial left position (align left edge of dropdown with left edge of button)
          let left = rect.left + scrollX;

          // Check if dropdown would overflow right edge of viewport
          if (left + dropdownWidth > viewportWidth + scrollX) {
            // Position dropdown to align right edge with right edge of button
            // This ensures it doesn't overflow the right side
            left = rect.right + scrollX - dropdownWidth;
          }

          // Check if dropdown would overflow left edge of viewport
          if (left < scrollX) {
            // If it would overflow left, align with left edge of viewport with some padding
            left = scrollX + 8; // 8px padding from viewport edge
          }

          setDropdownPosition({
            top: rect.bottom + scrollY + 8, // 8px = mt-2 equivalent
            left,
          });
        }
      };

      updatePosition();

      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [showAdvanced]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        if (showAdvanced) {
          onShowAdvancedToggle();
        }
      }
    };

    if (showAdvanced) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdvanced, onShowAdvancedToggle]);

  // Close info popover when clicking outside
  useEffect(() => {
    if (!showInfo) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        infoButtonRef.current &&
        !infoButtonRef.current.contains(e.target as Node) &&
        infoPopoverRef.current &&
        !infoPopoverRef.current.contains(e.target as Node)
      ) {
        setShowInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInfo]);

  return (
    <div className={`flex flex-wrap items-center ${responsiveStyles.gap} w-full`}>
      {/* Advanced Dropdown Button */}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          data-testid="power-analysis-advanced-button"
          onClick={onShowAdvancedToggle}
          className={`${uiBtn('secondary', { size: 'sm' })} gap-2`}
          style={{
            fontSize: responsiveStyles.fontSize,
            padding: `${responsiveStyles.padding.y} ${responsiveStyles.padding.x}`,
            gap: '0.5rem',
          }}
        >
          <span>Advanced</span>
          <ChevronDown
            className={`text-gray-400 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
            style={{
              width: responsiveStyles.iconSize,
              height: responsiveStyles.iconSize,
            }}
          />
        </button>
        {showAdvanced &&
          dropdownPosition &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed bg-surface border border-border rounded-lg shadow-xl z-50 min-w-[280px] max-w-[320px]"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
              }}
            >
              <div className="p-4 space-y-3">
                {/* Leverage mode */}
                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-2">
                    Leverage Mode
                  </label>
                  <button
                    type="button"
                    onClick={onLeverModeToggle}
                    className={`${uiBtn('toggle', {
                      pressed: leverMode === 'gate',
                      size: 'sm',
                    })} w-full`}
                    title={
                      leverMode === 'gate'
                        ? 'Converting gate force to handle via outboard/inboard'
                        : 'Treating GateForceX as handle-equivalent (no lever scaling)'
                    }
                  >
                    {leverMode === 'gate' ? 'Gate → Handle' : 'Handle (no lever)'}
                  </button>
                </div>

                {/* Leverage suggestion */}
                {leverSuggestion && leverSuggestion.median && (
                  <div className="text-xs text-gray-600">
                    Leverage check: median peak/avg {leverSuggestion.median.toFixed(2)}
                    {leverSuggestion.suggestion && (
                      <>
                        {' '}
                        → suggests{' '}
                        <span className="font-medium">
                          {leverSuggestion.suggestion === 'handle'
                            ? 'Handle (no lever)'
                            : 'Gate → Handle'}
                        </span>
                      </>
                    )}
                    {leverSuggestion.suggestion && (
                      <button
                        onClick={() => {
                          const next = leverSuggestion.suggestion!;
                          setLeverMode(next);
                          onLeverModeToggle();
                        }}
                        className={`${uiBtn('outline', { size: 'sm' })} ml-2 px-2 py-0.5 text-xs`}
                      >
                        Apply
                      </button>
                    )}
                  </div>
                )}

                {/* Calibration */}
                {calibrationReady && !hideCalibrationControls && (
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-2">
                      Calibration
                    </label>
                    <PowerAnalysisCalibrationButtons
                      sessionId={sessionId}
                      hasCalibration={hasCalibration}
                      onCalibratingChange={onCalibratingChange}
                      onCalibrationComplete={onCalibrationComplete}
                      onCalibrationRemoved={onCalibrationRemoved}
                    />
                  </div>
                )}

                {/* Info text */}
                <p className="text-[11px] text-gray-500 pt-2 border-t border-gray-200">
                  <strong>Tip:</strong> Hold Shift and scroll to zoom. Shift + drag to zoom to a
                  time range.
                </p>
              </div>
            </div>,
            document.body
          )}
      </div>

      {/* Info icon with popover */}
      <div className="relative">
        <button
          ref={infoButtonRef}
          onClick={() => setShowInfo((prev) => !prev)}
          className={uiBtn('icon', { pressed: showInfo })}
          style={{
            padding: responsiveStyles.padding.y,
            width: `calc(${responsiveStyles.iconSize} + ${responsiveStyles.padding.x} * 2)`,
            height: `calc(${responsiveStyles.iconSize} + ${responsiveStyles.padding.y} * 2)`,
          }}
          title="Shortcuts & help"
          aria-label="Show shortcuts and help"
        >
          <Info
            style={{
              width: responsiveStyles.iconSize,
              height: responsiveStyles.iconSize,
            }}
          />
        </button>
        {showInfo && (
          <div
            ref={infoPopoverRef}
            className="absolute right-0 top-full mt-2 z-50 bg-surface border border-border rounded-lg shadow-xl w-[280px]"
          >
            <div className="p-3 space-y-3 text-xs text-gray-700">
              <div>
                <h4 className="font-semibold text-gray-900 mb-1.5">Keyboard Shortcuts</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Play / Pause</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] font-mono">
                      Space
                    </kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>Reset</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] font-mono">
                      R
                    </kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>Prev / Next stroke</span>
                    <div className="flex gap-1">
                      <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] font-mono">
                        &larr;
                      </kbd>
                      <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] font-mono">
                        &rarr;
                      </kbd>
                    </div>
                  </div>
                </div>
              </div>
              <hr className="border-gray-200" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1.5">Modes</h4>
                <div className="space-y-1">
                  <p>
                    <span className="font-medium">Per Stroke</span> — Steps through strokes one at a
                    time. Use arrow keys to navigate.
                  </p>
                  <p>
                    <span className="font-medium">Normal</span> — Continuous playback at the
                    selected speed.
                  </p>
                </div>
              </div>
              <hr className="border-gray-200" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1.5">Interactions</h4>
                <div className="space-y-1">
                  <p>
                    Shift + scroll to zoom &middot; Drag to pan &middot; Shift + drag to box-zoom
                  </p>
                  <p className="text-gray-500">
                    Plain scroll moves the page; hold Shift while scrolling over the chart to zoom.
                  </p>
                  <p>Click a rower in the legend to solo it; click more to compare</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Calibration status badge */}
      {hasCalibration && (
        <span
          data-testid="power-calibration-status"
          className="inline-flex items-center rounded-full border border-border bg-surface-elevated font-medium text-content-secondary"
          style={{
            fontSize: responsiveStyles.fontSize,
            padding: `${responsiveStyles.padding.y} ${responsiveStyles.padding.x}`,
            gap: '0.375rem',
          }}
          title={`Calibrated${method ? ` • ${method}` : ''}${
            isFinite(alpha) ? ` • α=${alpha.toFixed(3)}` : ''
          }`}
        >
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
          Calibrated
        </span>
      )}
    </div>
  );
};

export default PowerAnalysisControls;
